import { useId } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2 as ApprovedIcon,
  Clock3 as PendingIcon,
  FileText as FileTextIcon,
  Layers as TotalIcon,
  Loader as LoaderIcon,
  Plus as PlusIcon,
  RefreshCw as RefreshCwIcon,
  Search as SearchIcon,
  X as ClearIcon,
  XCircle as RejectedIcon,
} from "lucide-react";
import { Button } from "../../../components/ui/button/index.js";
import { SimpleSelect } from "../../../components/ui/select/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import { TechnicalValue } from "../TechnicalValue.js";
import { findLanguageLabel } from "../template-management/template-language-select-state.js";
import type { TemplateLanguageOption } from "../template-management/template-language-options.js";
import {
  TEMPLATE_LIST_FILTER_ALL,
  formatTemplateListCategoryToken,
  hasActiveTemplateListFilters,
  normalizeTemplateListStatus,
  splitLanguageOptionLabel,
  templateListCategoryI18nKey,
  templateListStatusLabelKey,
  toggleTemplateStatusFilter,
  type TemplateLibraryStatusSummary,
  type TemplateListViewFilters,
} from "../template-list-presentation.js";

const spinClass = "animate-[spin_0.8s_linear_infinite]";
const selectTriggerClass =
  "h-8 min-w-0 w-full overflow-hidden rounded-md border-line bg-surface px-2.5 py-0 text-[0.8125rem] font-medium leading-snug text-ink shadow-sm hover:border-line-strong sm:w-40 data-[placeholder]:font-normal data-[placeholder]:text-ink-muted [&>span]:min-w-0 [&>span]:truncate";
const selectItemClass =
  "rounded-md py-1.5 text-[0.8125rem] font-medium leading-snug text-ink data-[state=checked]:bg-accent-soft data-[state=checked]:text-accent";
const selectContentClass =
  "min-w-[12rem] max-h-[min(20rem,var(--radix-select-content-available-height))] rounded-lg border-line p-0.5 shadow-lg";

const KPI_TONES = {
  total: {
    icon: TotalIcon,
    iconClass: "bg-accent-soft text-accent",
    valueClass: "text-ink",
    activeClass: "border-accent ring-accent/20",
  },
  approved: {
    icon: ApprovedIcon,
    iconClass: "bg-success-muted text-success",
    valueClass: "text-success",
    activeClass: "border-success ring-success/20",
  },
  pending: {
    icon: PendingIcon,
    iconClass: "bg-warning-muted text-warning",
    valueClass: "text-warning",
    activeClass: "border-warning ring-warning/20",
  },
  rejected: {
    icon: RejectedIcon,
    iconClass: "bg-error-muted text-error",
    valueClass: "text-error",
    activeClass: "border-error ring-error/20",
  },
} as const;

const PRIMARY_STATUSES = ["APPROVED", "PENDING", "REJECTED"];

export function TemplateListHeader({
  showManageActions,
  showRefresh,
  syncing,
  filters,
  statusOptions,
  categoryOptions,
  languageFilterValues,
  languageOptions,
  lastSyncedLabel,
  templateCount,
  statusSummary,
  onFiltersChange,
  onCreate,
  onRefresh,
}: {
  showManageActions: boolean;
  showRefresh: boolean;
  syncing: boolean;
  filters: TemplateListViewFilters;
  statusOptions: readonly string[];
  categoryOptions: readonly string[];
  languageFilterValues: readonly string[];
  languageOptions: readonly TemplateLanguageOption[];
  lastSyncedLabel: string | null;
  templateCount: number | null;
  statusSummary: TemplateLibraryStatusSummary | null;
  onFiltersChange: (filters: TemplateListViewFilters) => void;
  onCreate: () => void;
  onRefresh: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const searchId = useId();
  const refreshLabel = syncing
    ? t("whatsapp.templates.syncing")
    : t("whatsapp.templates.refresh");
  const filtersActive = hasActiveTemplateListFilters(filters);

  function statusLabel(status: string): string {
    const key = templateListStatusLabelKey(status);
    const translated = t(key);
    return translated === key ? status : translated;
  }

  function categoryLabel(category: string): string {
    const key = templateListCategoryI18nKey(category);
    const translated = t(key);
    return translated === key ? formatTemplateListCategoryToken(category) : translated;
  }

  const otherStatuses = statusOptions.filter(
    (status) => !PRIMARY_STATUSES.includes(normalizeTemplateListStatus(status)),
  );

  return (
    <div className="grid gap-4 border-b border-line px-4 pt-4 pb-3 sm:px-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3 text-start">
          <span
            className="mt-0.5 hidden size-10 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent sm:flex"
            aria-hidden="true"
          >
            <FileTextIcon size={20} />
          </span>
          <div className="grid min-w-0 gap-1">
            <h2
              id="whatsapp-templates-heading"
              className="m-0 text-lg leading-tight font-semibold tracking-[-0.015em] text-ink"
            >
              {t("whatsapp.templates.title")}
            </h2>
            <p className="m-0 max-w-[36rem] text-[0.8125rem] leading-snug text-ink-muted">
              {t("whatsapp.templates.subtitle")}
            </p>
            {templateCount != null || lastSyncedLabel ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.75rem] text-ink-muted">
                {templateCount != null ? (
                  <span className="inline-flex items-center rounded-full bg-surface-muted px-2 py-0.5 font-medium text-ink">
                    {t("whatsapp.templates.list.libraryCount", { count: templateCount })}
                  </span>
                ) : null}
                {lastSyncedLabel ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="size-1.5 rounded-full bg-success" aria-hidden="true" />
                    {lastSyncedLabel}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {showManageActions || showRefresh ? (
          <div className="flex w-full min-w-0 items-center justify-end gap-2 sm:w-auto">
            {showRefresh ? (
              <SimpleTooltip content={refreshLabel}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 shrink-0 gap-2 rounded-lg border-line bg-surface px-3.5 font-medium shadow-sm transition-[box-shadow,transform,border-color] duration-150 hover:-translate-y-px hover:border-line-strong hover:bg-surface hover:shadow-md motion-reduce:hover:translate-y-0"
                  onClick={onRefresh}
                  disabled={syncing}
                  aria-label={refreshLabel}
                >
                  {syncing ? (
                    <LoaderIcon size={15} className={spinClass} aria-hidden="true" />
                  ) : (
                    <RefreshCwIcon size={15} aria-hidden="true" />
                  )}
                  <span className="hidden sm:inline">{refreshLabel}</span>
                </Button>
              </SimpleTooltip>
            ) : null}
            {showManageActions ? (
              <Button
                type="button"
                variant="default"
                size="sm"
                className="h-9 min-w-0 flex-1 gap-2 rounded-lg px-4 font-semibold shadow-md transition-[box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-lg motion-reduce:hover:translate-y-0 sm:flex-none"
                onClick={onCreate}
              >
                <PlusIcon size={16} aria-hidden="true" />
                {t("whatsapp.templates.manage.createAction")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {statusSummary ? (
        <div
          className="grid grid-cols-2 gap-2 lg:grid-cols-4"
          role="group"
          aria-label={t("whatsapp.templates.list.filterStatus")}
        >
          {(
            [
              ["total", null, statusSummary.total],
              ["approved", "APPROVED", statusSummary.approved],
              ["pending", "PENDING", statusSummary.pending],
              ["rejected", "REJECTED", statusSummary.rejected],
            ] as const
          ).map(([key, status, value]) => {
            const tone = KPI_TONES[key];
            const Icon = tone.icon;
            const active =
              status == null
                ? filters.status === TEMPLATE_LIST_FILTER_ALL
                : normalizeTemplateListStatus(filters.status) === status;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                className={cn(
                  "group flex items-center gap-3 rounded-lg border bg-surface px-3 py-2.5 text-start shadow-sm transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent motion-reduce:transition-none motion-reduce:hover:translate-y-0",
                  active ? cn("ring-2", tone.activeClass) : "border-line hover:border-line-strong",
                )}
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    status:
                      status == null
                        ? TEMPLATE_LIST_FILTER_ALL
                        : toggleTemplateStatusFilter(filters.status, status),
                  })
                }
              >
                <span
                  className={cn(
                    "flex size-8 shrink-0 items-center justify-center rounded-md",
                    tone.iconClass,
                  )}
                  aria-hidden="true"
                >
                  <Icon size={16} />
                </span>
                <span className="grid min-w-0 gap-0.5">
                  <span className={cn("text-xl leading-none font-semibold tabular-nums", tone.valueClass)}>
                    {value}
                  </span>
                  <span className="truncate text-[0.75rem] font-medium text-ink-muted">
                    {t(`whatsapp.templates.list.summary.${key}`)}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center">
        <div className="flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md border border-line bg-surface px-2.5 shadow-sm transition-[border-color,box-shadow] duration-150 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/15 md:max-w-sm">
          <SearchIcon size={14} className="shrink-0 text-ink-subtle" aria-hidden="true" />
          <input
            id={searchId}
            type="search"
            className="min-w-0 flex-1 appearance-none border-0 bg-transparent py-1 text-start text-[0.8125rem] leading-snug text-ink shadow-none outline-none placeholder:text-ink-subtle [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            value={filters.query}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder={t("whatsapp.templates.list.searchPlaceholder")}
            aria-label={t("whatsapp.templates.list.searchLabel")}
            onChange={(event) => onFiltersChange({ ...filters, query: event.target.value })}
          />
          {filters.query ? (
            <button
              type="button"
              className="flex size-5 shrink-0 items-center justify-center rounded-sm text-ink-subtle hover:bg-surface-hover hover:text-ink"
              aria-label={t("whatsapp.templates.list.clearFilters")}
              onClick={() => onFiltersChange({ ...filters, query: "" })}
            >
              <ClearIcon size={12} aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-2 sm:flex sm:shrink-0 md:ms-auto">
          {otherStatuses.length > 0 ? (
            <SimpleSelect
              ariaLabel={t("whatsapp.templates.list.filterStatus")}
              value={
                PRIMARY_STATUSES.includes(normalizeTemplateListStatus(filters.status))
                  ? TEMPLATE_LIST_FILTER_ALL
                  : filters.status
              }
              onValueChange={(status) => onFiltersChange({ ...filters, status })}
              triggerClassName={selectTriggerClass}
              itemClassName={selectItemClass}
              contentClassName={selectContentClass}
              options={[
                { value: TEMPLATE_LIST_FILTER_ALL, label: t("whatsapp.templates.list.allStatuses") },
                ...otherStatuses.map((status) => ({ value: status, label: statusLabel(status) })),
              ]}
            />
          ) : null}
          <SimpleSelect
            ariaLabel={t("whatsapp.templates.list.filterCategory")}
            value={filters.category}
            onValueChange={(category) => onFiltersChange({ ...filters, category })}
            triggerClassName={selectTriggerClass}
            itemClassName={selectItemClass}
            contentClassName={selectContentClass}
            options={[
              { value: TEMPLATE_LIST_FILTER_ALL, label: t("whatsapp.templates.list.allCategories") },
              ...categoryOptions.map((category) => ({
                value: category,
                label: categoryLabel(category),
              })),
            ]}
          />
          <SimpleSelect
            ariaLabel={t("whatsapp.templates.list.filterLanguage")}
            value={filters.language}
            onValueChange={(language) => onFiltersChange({ ...filters, language })}
            triggerClassName={selectTriggerClass}
            itemClassName={selectItemClass}
            contentClassName={selectContentClass}
            options={[
              { value: TEMPLATE_LIST_FILTER_ALL, label: t("whatsapp.templates.list.allLanguages") },
              ...languageFilterValues.map((code) => {
                const parts = splitLanguageOptionLabel(
                  findLanguageLabel(languageOptions, code),
                  code,
                );
                return {
                  value: code,
                  label: parts.localized,
                  detail:
                    parts.localized === parts.code ? undefined : (
                      <TechnicalValue className="text-[0.6875rem] font-normal leading-none text-ink-subtle">
                        {parts.code}
                      </TechnicalValue>
                    ),
                };
              }),
            ]}
          />
          {filtersActive ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="col-span-2 h-8 px-2.5 text-[0.8125rem] font-medium text-ink-muted hover:text-ink sm:col-span-1"
              onClick={() =>
                onFiltersChange({
                  query: "",
                  status: TEMPLATE_LIST_FILTER_ALL,
                  category: TEMPLATE_LIST_FILTER_ALL,
                  language: TEMPLATE_LIST_FILTER_ALL,
                })
              }
            >
              {t("whatsapp.templates.list.clearFilters")}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
